import { FormInput, FormSelect, LocaleEnum, PrimaryButton, getLocaleLanguage } from '@systemeio/ui'
import { updateBlog } from 'modules/blogs/api/blog-api'
import {
  BlogApiInterface,
  BlogEditInterface,
  BlogErrorsInterface,
  BlogInterface,
} from 'modules/blogs/types/blog-interface'
import React, { useState } from 'react'
import EditModal from 'shared/components/edit-modal'
import FormUrlPathInput from 'shared/components/form-url-path-input'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useMutationUpdate } from 'shared/hooks/use-mutation-update'
import { UpdateDataFunctionType } from 'shared/hooks/use-update-data'
import useUserDomains from 'shared/hooks/use-user-domains'
import { LocaleDataInterface } from 'shared/types/locale-data-interface'
import { fetchWithErrors } from 'shared/utils/fetch-with-errors'
import { KeyedMutator } from 'swr'
import { RssIcon, SettingsIcon } from '@systemeio/ui/icons'

interface BlogSettingsProps {
  blog?: BlogInterface
  updateData: UpdateDataFunctionType
  blogMutate: KeyedMutator<BlogInterface>
}

const defaultErrors: BlogErrorsInterface = {
  name: '',
  path: '',
  domain: '',
  locale: '',
  template: '',
}

const BlogSettings = ({ blog, updateData, blogMutate }: BlogSettingsProps) => {
  const mutationUpdate = useMutationUpdate()
  const { t } = useLocoTranslation()
  const [errors, setErrors] = useState<BlogErrorsInterface>(defaultErrors)
  const [settingsOpened, setSettingsOpened] = useState(false)
  const [shouldFetchDomains, setShouldFetchDomains] = useState(false)
  const { userDomainsSelect } = useUserDomains(shouldFetchDomains)
  const localeData: LocaleDataInterface[] = Object.values(LocaleEnum).map(locale => ({
    id: locale as LocaleEnum,
    caption: getLocaleLanguage(locale),
  }))
  const onEditBlog = async (data: BlogInterface) => {
    if (blog) {
      await fetchWithErrors(async () => {
        const newData: BlogEditInterface = {
          name: data.name,
          domain: data.domain.id,
          path: data.path,
          locale: data.locale,
        }
        const response = await updateBlog(blog.id, newData)
        await updateData(
          false,
          undefined,
          {
            mutationData: blog,
            onMutate: async () => {
              await blogMutate(() => ({ ...response.data }), false)
            },
          },
          {
            id: blog?.id,
            onMutate: async apiKey => {
              await mutationUpdate<BlogApiInterface>(
                apiKey,
                data =>
                  data && {
                    hasMore: data.hasMore,
                    items: data.items.map(item => {
                      if (item.id === response.data.id) item = response.data
                      return item
                    }),
                  },
              )
            },
          },
        )
      }, setErrors)
    }
  }

  const onModalClose = () => setErrors(defaultErrors)
  return (
    <>
      <PrimaryButton
        onClick={() => {
          setSettingsOpened(true)
          setShouldFetchDomains(true)
        }}
        disabled={!blog}
      >
        <SettingsIcon className={'fill-background-secondary'} />
        <span className={'hidden lg:flex'}>{t('dashboard.blog.blog_settings')}</span>
      </PrimaryButton>
      {blog && (
        <EditModal
          caption={t('dashboard.blog.title')}
          toastCaption={t('dashboard.blog.blog_updated')}
          afterLeave={onModalClose}
          opened={settingsOpened}
          onClose={() => setSettingsOpened(false)}
          instance={blog}
          editable={[
            [
              {
                field: 'name',
                onRender: (value, onChange) => (
                  <FormInput
                    key="blog-name-edit"
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    error={errors.name}
                    label={t('global.name')}
                  />
                ),
              },
              {
                field: 'domain',
                onRender: (value, onChange) => (
                  <FormSelect
                    withoutCloseIcon
                    isPreFetching={!userDomainsSelect}
                    key="blog-domain-edit"
                    onChange={value => value && onChange({ id: value, domainName: '' })}
                    value={value.id}
                    label={t('dashboard.blog.domain_name')}
                    data={userDomainsSelect}
                    error={errors.domain}
                  />
                ),
              },
            ],
            {
              field: 'path',
              onRender: (value, onChange, data) => {
                const domainName = userDomainsSelect?.find(el => {
                  const domain = data.domain
                  return el.id === domain.id
                })?.caption

                return (
                  <div key="blog-path-edit-and-rss-feed" className={'flex flex-col gap-5'}>
                    <FormUrlPathInput
                      label={t('global.url_path')}
                      error={errors.path}
                      value={value}
                      onChange={e => onChange(e.target.value)}
                      domainName={domainName}
                    />
                    <FormUrlPathInput
                      label={
                        <div className={'flex items-end gap-1.5'}>
                          {t('dashboard.blog.rss_feed')}
                          <RssIcon />
                        </div>
                      }
                      domainName={domainName}
                      value={`${data.path}${!!data.path.length ? '/' : ''}feed`}
                      disabled={true}
                    />
                  </div>
                )
              },
            },
            {
              field: 'locale',
              onRender: (value, onChange) => (
                <FormSelect
                  withoutCloseIcon
                  key="blog-locale-edit"
                  onChange={value => value && onChange(value)}
                  value={value}
                  label={t('global.language')}
                  data={localeData}
                  placeholder={t('dashboard.blog.choose_domain')}
                  error={errors.locale}
                />
              ),
            },
          ]}
          onEdit={data => onEditBlog(data)}
        />
      )}
    </>
  )
}

export default BlogSettings
